import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, CirclePlus } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { registerSectorsData } from "@/Api/sectors";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { UnitySelectField } from "@/components/UnitySelectField";

const schema = z.object({
  unity_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  description: z.string().optional(),
  max_daily_overtime_minutes: z.string().optional(),
});

type SchemaType = z.infer<typeof schema>;

export const AddBtn = ({ updateFront }) => {
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      unity_id: "",
      name: "",
      description: "",
      max_daily_overtime_minutes: "",
    },
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");

    const { data: apiResponse, ok } = await registerSectorsData({
      ...data,
      max_daily_overtime_minutes: data.max_daily_overtime_minutes?.trim()
        ? Number(data.max_daily_overtime_minutes)
        : null,
    });
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }

    updateFront((prev) => {
      if (prev?.data.length < 15)
        return {
          ...prev,
          data: [apiResponse?.response, ...prev.data],
        };

      prev?.data.pop();
      return {
        ...prev,
        data: [apiResponse?.response, ...prev.data],
        last_page: prev.last_page + 1,
      };
    });

    form.reset();
    setFormState("default");
    setIsOpen(false);
    toast({
      description: apiResponse?.message,
      action: <Check color="#1edc2b" />,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Adicionar Setor
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Setor
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-3"
        >
          <Form.Row>
            <UnitySelectField />
          </Form.Row>

          <Form.Row>
            <Form.Field name="name">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Nome <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} placeholder="Insira um nome" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Textarea name="description" placeholder="Insira uma descrição (opcional)">
              Descrição
            </Form.Textarea>
          </Form.Row>

          <Form.Row>
            <Form.Field name="max_daily_overtime_minutes">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Limite diário de hora extra (minutos)
                  </Form.Label>
                  <Form.Control>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      max={600}
                      placeholder="Deixe em branco para usar o padrão do sistema"
                    />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Adicionando <LoaderSpin />
                </>
              )}
              {formState === "default" && "Adicionar"}
              {formState === "error" && "Erro ao adicionar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
